import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";

const AddTransaction = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const server_url = localStorage.getItem("server_url");
  const company_id = localStorage.getItem("company_id") || "2";
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const grage_id = userData.grage_id || "1";

  // Check if we're editing an existing transaction
  const isEdit = location.pathname.includes("edit");
  const existingTransaction = location.state?.transaction;

  // Form state
  const [formData, setFormData] = useState({
    amount: "",
    remark: "",
    transaction_date: new Date().toISOString().split("T")[0],
    transaction_type: "2",
    expense_category_id: "",
    transaction_grage_id: grage_id,
  });

  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Searchable Dropdown Component
  const SearchableDropdown = ({
    options = [],
    value,
    onChange,
    placeholder,
    name,
    error,
    loading,
    disabled,
    required,
  }) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const dropdownRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }, [searchTerm, options]);

    useEffect(() => {
      const selected = options.find((opt) => opt.value == value);
      setSearchTerm(selected ? selected.label : "");
    }, [value, options]);

    const handleInputChange = (e) => {
      const term = e.target.value;
      setSearchTerm(term);
      setIsOpen(true);
    };

    const handleSelect = (selectedValue) => {
      setIsOpen(false);
      if (onChange) {
        onChange({ target: { name, value: selectedValue } });
      }
    };

    const displayValue = options.find((opt) => opt.value == value)?.label || "";
    const inputValue = isOpen ? searchTerm : displayValue;

    return (
      <div className="searchable-dropdown" ref={dropdownRef}>
        <div className={`input-group ${error ? "error" : ""}`}>
          <div className="input-icon">
            <i className="fas fa-tag"></i>
          </div>
          <input
            type="text"
            className="form-input"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            disabled={disabled || loading}
            readOnly={!isOpen}
            required={required}
          />
          {isOpen && (
            <ul
              className="dropdown-list"
              style={{ width: "100%", textAlign: "left" }}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <li
                    style={{
                      padding: "10px 12px",
                      margin: "0",
                      cursor: "pointer",
                      borderBottom: "1px solid #f1f1f1",
                      fontSize: "14px",
                    }}
                    key={option.value}
                    className="dropdown-item"
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.label}
                  </li>
                ))
              ) : (
                <li
                  className="dropdown-item no-results"
                  style={{
                    padding: "10px 12px",
                    color: "#666",
                    textAlign: "center",
                  }}
                >
                  No results found
                </li>
              )}
            </ul>
          )}
        </div>
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}
        {loading && <div className="loading-message">Loading...</div>}
      </div>
    );
  };

  // Populate form if editing and fetch categories
  useEffect(() => {
    fetchCategories();

    if (isEdit && existingTransaction) {
      setFormData({
        amount: existingTransaction.amount || "",
        remark: existingTransaction.remark || "",
        transaction_date:
          existingTransaction.transaction_date ||
          new Date().toISOString().split("T")[0],
        transaction_type: existingTransaction.transaction_type || "2",
        expense_category_id: existingTransaction.expense_category_id || "",
        transaction_grage_id:
          existingTransaction.transaction_grage_id || grage_id,
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        transaction_grage_id: grage_id,
      }));
    }
  }, [isEdit, existingTransaction, grage_id]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("company_id", company_id);
      formDataToSend.append("transaction_grage_id", grage_id);
      formDataToSend.append("action", "expense_category_list");

      const response = await axios.post(
        `${server_url}other_transaction.php`,
        formDataToSend,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status === "1") {
        const categoryOptions = (response.data.result || []).map(
          (category) => ({
            value: category.expenses_categories_id,
            label: category.expenses_categories_title,
          })
        );
        setCategories(categoryOptions);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error("Fetch categories error:", error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validate amount to allow only numbers and decimal
    if (name === "amount") {
      // Allow only numbers and one decimal point
      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        setFormData((prev) => ({
          ...prev,
          [name]: value,
        }));
      }
    } else if (name === "transaction_type") {
      // When transaction type changes, clear expense category if switching to income
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        expense_category_id: value === "1" ? "" : prev.expense_category_id,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.amount.trim()) {
      newErrors.amount = "Amount is required";
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    } else if (isNaN(parseFloat(formData.amount))) {
      newErrors.amount = "Please enter a valid amount";
    }

    if (!formData.remark.trim()) {
      newErrors.remark = "Remark is required";
    }

    if (!formData.transaction_date) {
      newErrors.transaction_date = "Transaction date is required";
    }

    if (!formData.transaction_type) {
      newErrors.transaction_type = "Transaction type is required";
    }

    // Only validate expense category for "out" transactions
    if (formData.transaction_type === "2" && !formData.expense_category_id) {
      newErrors.expense_category_id = "Category is required for expenses";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("company_id", company_id);
      formDataToSend.append("amount", formData.amount);
      formDataToSend.append("remark", formData.remark);
      formDataToSend.append("transaction_date", formData.transaction_date);
      formDataToSend.append("transaction_type", formData.transaction_type);

      // Only send expense_category_id if it has a value
      if (formData.expense_category_id) {
        formDataToSend.append(
          "expense_category_id",
          formData.expense_category_id
        );
      } else {
        // Send empty string if no category selected
        formDataToSend.append("expense_category_id", "");
      }

      formDataToSend.append(
        "transaction_grage_id",
        formData.transaction_grage_id
      );

      // Add action and transaction_id if editing
      if (isEdit && existingTransaction) {
        formDataToSend.append("action", "update");
        formDataToSend.append(
          "transaction_id",
          existingTransaction.transaction_id
        );
      } else {
        formDataToSend.append("action", "add");
      }

      const response = await axios.post(
        `${server_url}other_transaction.php`,
        formDataToSend,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status === "1") {
        toast.success(
          response.data.message ||
            (isEdit
              ? "Transaction updated successfully!"
              : "Transaction added successfully!")
        );
        navigate("/transaction");
      } else {
        toast.error(
          response.data.error ||
            `Failed to ${isEdit ? "update" : "add"} transaction`
        );
      }
    } catch (error) {
      console.error("Save transaction error:", error);
      toast.error(
        `Unable to ${isEdit ? "update" : "add"} transaction. Please try again.`
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB");
  };

  // If no existing data in edit mode
  if (isEdit && !existingTransaction) {
    return (
      <div className="login-container">
        <div className="login-form-container">
          <div style={{ textAlign: "center", padding: "40px" }}>
            <h3>No transaction data found</h3>
            <p>Please go back and select a transaction to edit.</p>
            <Link
              to="/transaction"
              style={{ color: "#0352a1", textDecoration: "none" }}
            >
              <i className="fas fa-arrow-left"></i> Back to Transactions
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-form-container">
        <div className="login-form-wrapper">
          {/* Transaction Info Card for Edit Mode */}
          {isEdit && existingTransaction && (
            <div
              className="transaction-info-card"
              style={{
                marginBottom: "20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "12px",
                padding: "15px",
                borderLeft: `4px solid ${
                  existingTransaction.transaction_type === "1"
                    ? "#10b981"
                    : "#dc2626"
                }`,
              }}
            >
              <div
                className="transaction-header"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    fontSize: "16px",
                    color: "#2d3748",
                  }}
                >
                  {existingTransaction.transaction_type === "1" ? (
                    <span style={{ color: "#10b981" }}>INCOME</span>
                  ) : (
                    <span style={{ color: "#dc2626" }}>EXPENSE</span>
                  )}
                </div>
                <div
                  style={{
                    color:
                      existingTransaction.transaction_type === "1"
                        ? "#10b981"
                        : "#dc2626",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                >
                  {existingTransaction.transaction_type === "1" ? "+" : "-"}₹
                  {parseFloat(existingTransaction.amount || 0).toLocaleString(
                    "en-IN",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }
                  )}
                </div>
              </div>

              <div
                className="transaction-details"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                {existingTransaction.expenses_categories_title && (
                  <div style={{ fontSize: "14px", color: "#4a5568" }}>
                    <i
                      className="fas fa-tag"
                      style={{ marginRight: "5px", color: "#4a5568" }}
                    ></i>
                    {existingTransaction.expenses_categories_title}
                  </div>
                )}

                <div style={{ fontSize: "14px", color: "#4a5568" }}>
                  <i
                    className="fas fa-sticky-note"
                    style={{ marginRight: "5px" }}
                  ></i>
                  {existingTransaction.remark || "No remark"}
                </div>

                <div
                  style={{
                    fontSize: "14px",
                    color: "#4a5568",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    <i
                      className="far fa-calendar"
                      style={{ marginRight: "5px" }}
                    ></i>
                    {formatDate(existingTransaction.transaction_date)}
                  </span>
                  <span style={{ fontSize: "12px", color: "#999" }}>
                    <i
                      className="fas fa-history"
                      style={{ marginRight: "5px" }}
                    ></i>
                    {formatDate(existingTransaction.c_date)}
                  </span>
                </div>
              </div>
            </div>
          )}

          <form className="login-form" onSubmit={handleSubmit}>
            {/* Transaction Type */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "10px",
                  fontWeight: "bold",
                }}
              >
                Transaction Type *
              </label>
              <div style={{ display: "flex", gap: "15px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "8px",
                      border: `2px solid ${
                        formData.transaction_type === "2"
                          ? "#dc2626"
                          : "#e2e8f0"
                      }`,
                      background:
                        formData.transaction_type === "2" ? "#fee2e2" : "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      transition: "all 0.3s",
                    }}
                  >
                    <input
                      type="radio"
                      name="transaction_type"
                      value="2"
                      checked={formData.transaction_type === "2"}
                      onChange={handleChange}
                      style={{ display: "none" }}
                    />
                    <i
                      className="fas fa-arrow-up"
                      style={{ color: "#dc2626" }}
                    ></i>
                    <span>Expense</span>
                  </div>
                </label>

                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderRadius: "8px",
                      border: `2px solid ${
                        formData.transaction_type === "1"
                          ? "#10b981"
                          : "#e2e8f0"
                      }`,
                      background:
                        formData.transaction_type === "1" ? "#d1fae5" : "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      transition: "all 0.3s",
                    }}
                  >
                    <input
                      type="radio"
                      name="transaction_type"
                      value="1"
                      checked={formData.transaction_type === "1"}
                      onChange={handleChange}
                      style={{ display: "none" }}
                    />
                    <i
                      className="fas fa-arrow-down"
                      style={{ color: "#10b981" }}
                    ></i>
                    <span>Income</span>
                  </div>
                </label>
              </div>
              {errors.transaction_type && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.transaction_type}
                </div>
              )}
            </div>

            {/* Amount */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "0px",
                  fontWeight: "bold",
                }}
              >
                Amount *
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-rupee-sign"></i>
                </div>
                <input
                  type="text"
                  name="amount"
                  className={`form-input ${errors.amount ? "error" : ""}`}
                  placeholder="Enter amount"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                />
              </div>
              {errors.amount && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.amount}
                </div>
              )}
            </div>

            {/* Transaction Date */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "0px",
                  fontWeight: "bold",
                }}
              >
                Transaction Date *
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <input
                  type="date"
                  name="transaction_date"
                  className={`form-input ${
                    errors.transaction_date ? "error" : ""
                  }`}
                  value={formData.transaction_date}
                  onChange={handleChange}
                  required
                />
              </div>
              {errors.transaction_date && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.transaction_date}
                </div>
              )}
            </div>

            {/* Category - Only show for expense transactions */}
            {formData.transaction_type === "2" && (
              <div className="form-group">
                <label
                  style={{
                    textAlign: "left",
                    display: "block",
                    marginBottom: "0px",
                    fontWeight: "bold",
                  }}
                >
                  Expense Category *
                </label>
                <SearchableDropdown
                  name="expense_category_id"
                  options={categories}
                  value={formData.expense_category_id}
                  onChange={handleChange}
                  placeholder="Select Expense Category"
                  error={errors.expense_category_id}
                  loading={loading}
                  required={formData.transaction_type === "2"}
                />
              </div>
            )}

            {/* Remark */}
            <div className="form-group">
              <label
                style={{
                  textAlign: "left",
                  display: "block",
                  marginBottom: "0px",
                  fontWeight: "bold",
                }}
              >
                Remark *
              </label>
              <div className="input-group">
                <div className="input-icon">
                  <i className="fas fa-comment-alt"></i>
                </div>
                <textarea
                  name="remark"
                  className={`form-input textarea ${
                    errors.remark ? "error" : ""
                  }`}
                  placeholder="Enter transaction remark"
                  value={formData.remark}
                  onChange={handleChange}
                  rows="3"
                  required
                />
              </div>
              {errors.remark && (
                <div className="error-message">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.remark}
                </div>
              )}
            </div>

            {/* Hidden garage_id */}
            <input
              type="hidden"
              name="transaction_grage_id"
              value={formData.transaction_grage_id}
            />

            {/* Submit Button */}
            <div className="form-group" style={{ display: "ruby" }}>
              <button
                type="submit"
                className="login-button"
                disabled={submitting || loading}
                style={{
                  width: "48%",
                }}
              >
                <i className="fas fa-save"></i>
                {submitting
                  ? "Saving..."
                  : isEdit
                  ? "Update Transaction"
                  : "Add Transaction"}
              </button>

              {/* Cancel Button */}
              <button
                type="button"
                onClick={() => navigate("/transaction")}
                style={{
                  width: "48%",
                  background: "#e2e8f0",
                  color: "#4a5568",
                  border: "none",
                  marginLeft: "10px",
                  padding: "12px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  marginTop: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                }}
              >
                <i className="fas fa-times"></i>
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <style jsx>{`
        .login-container {
          min-height: 55vh;
          background: #f8f9fa;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .login-form-container {
          width: 100%;
          max-width: 600px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        @media (max-width: 768px) {
          .login-form-container {
            flex: none;
            padding: 0px;
          }
        }

        .login-form-wrapper {
          padding: 20px;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 0px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group:last-child {
          margin-bottom: 0;
        }

        label {
          font-weight: 600;
          color: #4a5568;
          font-size: 14px;
        }

        .input-group {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 12px;
          color: #a0aec0;
          z-index: 1;
          font-size: 16px;
        }

        .form-input {
          width: 100%;
          padding: 10px 12px 10px 40px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.3s;
          background: white;
        }

        .form-input:focus {
          outline: none;
          border-color: #0352a1;
          box-shadow: 0 0 0 3px rgba(3, 82, 161, 0.1);
        }

        .form-input.error {
          border-color: #dc2626;
        }

        .form-input.textarea {
          min-height: 80px;
          resize: vertical;
          padding-top: 10px;
          padding-bottom: 10px;
        }

        .form-input:disabled {
          background: #f8f9fa;
          cursor: not-allowed;
        }

        select.form-input {
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%23a0aec0' viewBox='0 0 16 16'%3E%3Cpath d='M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 16px;
          padding-right: 40px;
        }

        /* Searchable Dropdown Styles */
        .searchable-dropdown {
          position: relative;
          width: 100%;
        }

        .dropdown-list {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          max-height: 200px;
          overflow-y: auto;
          z-index: 1000;
          margin-top: 2px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          list-style: none;
          padding: 0;
        }

        .dropdown-item {
          padding: 8px 12px;
          cursor: pointer;
          transition: background 0.2s;
          font-size: 14px;
          color: #4a5568;
        }

        .dropdown-item:hover {
          background: #f7fafc;
        }

        .dropdown-item.no-results {
          color: #a0aec0;
          text-align: center;
          cursor: default;
        }

        .error-message {
          color: #dc2626;
          font-size: 12px;
          margin-top: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .loading-message {
          color: #666;
          font-size: 12px;
          margin-top: 4px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .login-button {
          background: #0352a1;
          color: white;
          border: none;
          padding: 12px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-top: 10px;
        }

        .login-button:hover:not(:disabled) {
          background: #024080;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(3, 82, 161, 0.2);
        }

        .login-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .login-form-wrapper {
            padding: 15px;
          }

          .login-container {
            padding: 10px;
          }

          .form-group {
            margin-bottom: 15px;
          }
        }

        @media (max-width: 480px) {
          .login-form-wrapper {
            padding: 12px;
          }

          .form-input {
            padding: 8px 10px 8px 36px;
            font-size: 13px;
          }

          .input-icon {
            left: 10px;
            font-size: 14px;
          }

          .login-button {
            padding: 10px;
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default AddTransaction;
